import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd';
import { useCallback, useContext, useMemo } from 'react';

import { CLIENTS_FILTER, VISITS_FILTER } from '@app/constants';
import {
  initialClientsFilter,
  initialFilterGroups,
  initialVisitsFilter,
} from '@app/data';
import { useContentWidth, useMemoizeObject, useWindowSize } from '@app/hooks';
import { ClientFiltersContext } from '@app/providers';
import type { NClientFiltersProvider } from '@app/providers/ClientFiltersProvider/ClientFiltersProvider.types';
import { createId, generateKey } from '@app/utils';

import {
  calculateServerFilters,
  generateLastMonthPeriod,
  generateLastThreeMonths,
} from '@pages/Clients/ClientsList/utils';

import { AdaptedSelect } from '@pages/Clients/ClientsList/components/Filters';

import {
  filtersByClients,
  filtersByVisits,
} from '@pages/Clients/ClientsList//data';
import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';
import { initialFiltersConfig } from '@pages/Clients/ClientsList/data/initialFiltersConfig';

import type { MenuProps } from 'antd';

const SECOND_LOGICAL_GROUP_ID = '1bf95fa1-83fe-41d2-bd66-df6c7646be1f';
const SECOND_OBJECT_BY_VISITS_ID = 'e2a98b09-f569-4da5-b956-558cacf322c3';
const THIRD_OBJECT_BY_CLIENTS_ID = 'cc00b21e-8369-4c04-91b8-85c340cd1fd9';
const FOURTH_OBJECT_BY_CLIENTS_ID = '127a00e9-f8a4-4521-bd64-e559a54a7ade';
const SPECIFIC_FILTER = 'specific-filter';

const clientsFilterForSegments = [
  ...initialClientsFilter,
  {
    ...initialClientsFilter[0],
    FilterGroupsId: SECOND_LOGICAL_GROUP_ID,
    SpecificFilterId: THIRD_OBJECT_BY_CLIENTS_ID,
  },
  {
    ...initialClientsFilter[1],
    FilterGroupsId: SECOND_LOGICAL_GROUP_ID,
    SpecificFilterId: FOURTH_OBJECT_BY_CLIENTS_ID,
  },
];

export const useFilters = () => {
  const {
    filters,
    serverFilters,
    filterGroups,
    tagIncludingStatuses,
    setFilters,
    setFilterGroups,
    setServerFilters,
    setTagIncludingStatuses,
    deleteTagsIncludingStatus,
  } = useContext(ClientFiltersContext);
  const { innerWidth } = useWindowSize();
  const { contentWidth } = useContentWidth();
  const initialVisitsFilterValues = initialVisitsFilter[0]?.Values?.[0];

  const addFilterGroups = useCallback(
    (isAnd: boolean) => {
      const filtersGroupId = generateKey(SPECIFIC_FILTER, createId());

      setFilters(generateNewFilterGroups(filters, filtersGroupId, isAnd));

      setServerFilters(
        generateNewFilterGroups(serverFilters, filtersGroupId, isAnd)
      );

      setFilterGroups([
        ...(filterGroups || []),
        {
          id: filtersGroupId,
          isAnd,
        },
      ]);

      setTagIncludingStatuses([...tagIncludingStatuses, true]);
    },
    [
      filters,
      serverFilters,
      filterGroups,
      tagIncludingStatuses,
      setFilters,
      setFilterGroups,
      setServerFilters,
      setTagIncludingStatuses,
    ]
  );

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: <span onClick={() => addFilterGroups(true)}>Тип связи "И"</span>,
      },
      {
        key: '2',
        label: (
          <span onClick={() => addFilterGroups(false)}>Тип связи "ИЛИ"</span>
        ),
      },
    ],
    [addFilterGroups]
  );

  const memoizedItems = useMemoizeObject(items || []);
  const memoizedFilterGroups = useMemoizeObject(filterGroups);
  const memoizedServerFilters =
    useMemoizeObject(serverFilters) || initialFiltersConfig;

  const generateNewFilterGroups: NClientsList.GenerateNewFilterGroups = (
    someFilters,
    filtersGroupId,
    isAnd
  ) => ({
    VisitsFilter: [
      ...(someFilters.VisitsFilter || []),
      {
        FilterGroupsId: filtersGroupId,
        SpecificFilterId: generateKey(SPECIFIC_FILTER, createId()),
        IsAnd: isAnd,
        Values: initialVisitsFilter?.[0]?.Values,
      },
    ],
    ClientsFilter: [
      ...(someFilters.ClientsFilter || []),
      {
        FilterGroupsId: filtersGroupId,
        SpecificFilterId: generateKey(SPECIFIC_FILTER, createId()),
        IsAnd: isAnd,
        IsInclude: false,
        Values: initialClientsFilter?.[0]?.Values,
      },
      {
        FilterGroupsId: filtersGroupId,
        SpecificFilterId: generateKey(SPECIFIC_FILTER, createId()),
        IsAnd: isAnd,
        IsInclude: true,
        Values: initialClientsFilter?.[1]?.Values,
      },
    ],
  });

  const generateSegmentLeadsData: NClientsList.GenerateSegmentLeadsData = (
    someFilters
  ) => {
    return {
      VisitsFilter: [
        {
          ...initialVisitsFilter[0],
          Values: [
            {
              ...initialVisitsFilter?.[0]?.Values[0],
              HasRecords: false,
            },
          ],
        },
      ],
      ClientsFilter: [...(someFilters.ClientsFilter || [])],
    };
  };

  const generateSegmentNewData: NClientsList.GenerateTypicalSegment = () => {
    return {
      VisitsFilter: [
        {
          ...initialVisitsFilter[0],
          Values: [
            {
              ...initialVisitsFilterValues,
              HasRecords: true,
              Statuses: ['visited'],
              DateStart: generateLastMonthPeriod().startDateInIso,
              DateEnd: generateLastMonthPeriod().endDateInIso,
            },
          ],
        },
        {
          ...initialVisitsFilter[0],
          FilterGroupsId: SECOND_LOGICAL_GROUP_ID,
          SpecificFilterId: SECOND_OBJECT_BY_VISITS_ID,
          Values: [
            {
              ...initialVisitsFilterValues,
              HasRecords: false,
              Statuses: ['visited'],
              DateEnd: generateLastMonthPeriod().startDateInIso,
            },
          ],
        },
      ],
      ClientsFilter: clientsFilterForSegments,
    };
  };

  const generateSegmentRepeatedData: NClientsList.GenerateTypicalSegment =
    () => {
      return {
        VisitsFilter: [
          {
            ...initialVisitsFilter[0],
            Values: [
              {
                ...initialVisitsFilterValues,
                HasRecords: true,
                Statuses: ['visited'],
                DateStart: generateLastMonthPeriod().startDateInIso,
                DateEnd: generateLastMonthPeriod().endDateInIso,
              },
            ],
          },
          {
            ...initialVisitsFilter[0],
            FilterGroupsId: SECOND_LOGICAL_GROUP_ID,
            SpecificFilterId: SECOND_OBJECT_BY_VISITS_ID,
            Values: [
              {
                ...initialVisitsFilterValues,
                HasRecords: true,
                Statuses: ['visited'],
                DateEnd: generateLastMonthPeriod().startDateInIso,
              },
            ],
          },
        ],
        ClientsFilter: clientsFilterForSegments,
      };
    };

  const generateSegmentLostData: NClientsList.GenerateTypicalSegment = () => {
    return {
      VisitsFilter: [
        {
          ...initialVisitsFilter[0],
          Values: [
            {
              ...initialVisitsFilterValues,
              HasRecords: true,
              Statuses: ['visited'],
              DateStart: generateLastThreeMonths().firstDateInIso,
              DateEnd: generateLastThreeMonths().secondDateInIso,
            },
          ],
        },
        {
          ...initialVisitsFilter[0],
          FilterGroupsId: SECOND_LOGICAL_GROUP_ID,
          SpecificFilterId: SECOND_OBJECT_BY_VISITS_ID,
          Values: [
            {
              ...initialVisitsFilterValues,
              HasRecords: false,
              Statuses: ['visited'],
              DateStart: generateLastThreeMonths().thirdDateInIso,
            },
          ],
        },
      ],
      ClientsFilter: clientsFilterForSegments,
    };
  };

  const generateListWithDeleted = (
    someFilters: NClientsList.Filters,
    id: string
  ) => {
    return {
      VisitsFilter: someFilters.VisitsFilter.filter(({ FilterGroupsId }) => {
        if (FilterGroupsId !== id) {
          return true;
        }
      }),
      ClientsFilter: someFilters.ClientsFilter.filter(({ FilterGroupsId }) => {
        if (FilterGroupsId !== id) {
          return true;
        }
      }),
    };
  };

  const deleteFilterGroups = useCallback(
    (globalFilterGroupsId: string) => {
      deleteTagsIncludingStatus(globalFilterGroupsId);

      setFilters(generateListWithDeleted(filters, globalFilterGroupsId));

      setServerFilters(
        generateListWithDeleted(serverFilters, globalFilterGroupsId)
      );

      setFilterGroups(
        filterGroups.filter(({ id: _id }) => _id !== globalFilterGroupsId)
      );
    },
    [
      filters,
      filterGroups,
      serverFilters,
      setFilters,
      setFilterGroups,
      setServerFilters,
      deleteTagsIncludingStatus,
    ]
  );

  const generateLeadsSegment = useCallback(() => {
    const initialFilterValues = {
      VisitsFilter: initialVisitsFilter,
      ClientsFilter: initialClientsFilter,
    };

    // ШАГ 1. Очистить все предыдущие фильтры
    setFilters(initialFilterValues);
    setServerFilters(initialFilterValues);
    setFilterGroups(initialFilterGroups);

    // ШАГ 2. Отрендерить группу новых фильтров
    setFilterGroups(initialFilterGroups);

    // ШАГ 3. Инициализировать объекты для новых фильтров
    setFilters(generateSegmentLeadsData(filters));
    setServerFilters(generateSegmentLeadsData(serverFilters));
  }, [filters, serverFilters, setFilterGroups, setFilters, setServerFilters]);

  const generateSegmentWithTwoGroups: NClientsList.GenerateSegmentWithTwoGroups =
    useCallback(
      (fn, isSecondGroup = true) => {
        const initialFilterValues = {
          VisitsFilter: initialVisitsFilter,
          ClientsFilter: initialClientsFilter,
        };

        // ШАГ 1. Очистить все предыдущие фильтры
        setFilters(initialFilterValues);
        setServerFilters(initialFilterValues);
        setFilterGroups(initialFilterGroups);

        // ШАГ 2. Отрендерить группу новых фильтров
        if (isSecondGroup) {
          setFilterGroups([
            ...initialFilterGroups,
            {
              id: SECOND_LOGICAL_GROUP_ID,
              isAnd: true,
            },
          ]);
        }

        // ШАГ 3. Инициализировать объекты для новых фильтров
        setFilters(fn());
        setServerFilters(fn());
      },
      [setFilterGroups, setFilters, setServerFilters]
    );

  const memoizedFilterStyles = useMemo(() => {
    let width = 0;
    let minWidth = 0;

    if (innerWidth > 950) {
      width = 225;
    } else if (792 < innerWidth && innerWidth <= 950) {
      width = contentWidth / 2 + 12;
    } else if (550 < innerWidth && innerWidth <= 792) {
      width = contentWidth + 36;
    } else {
      minWidth = contentWidth + 56;
    }

    return {
      width: `${width}px`,
      minWidth,
    };
  }, [innerWidth, contentWidth]);

  const filterGroupsComponent = useMemo(() => {
    const handledFilterGroups = memoizedFilterGroups || [];

    return handledFilterGroups.map(({ id, isAnd }, groupIndex) => {
      const areLastFilterGroups = groupIndex === handledFilterGroups.length - 1;
      const specificFilterId = Object.values(
        memoizedServerFilters.VisitsFilter
      ).find(({ FilterGroupsId }) => FilterGroupsId === id)?.SpecificFilterId;

      const renderPlaceholder = (
        placeholderStart: 'По визитам' | 'По клиентам',
        filterGroupKey: NClientFiltersProvider.FilterGroupKey
      ) => (
        <>
          <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
            {placeholderStart}
          </span>
          <span style={{ color: '#bfbfbf' }}>
            {calculateServerFilters({
              serverFilters: memoizedServerFilters,
              filterGroupKey,
              groupIndex,
            })}
          </span>
        </>
      );

      return (
        <Space
          key={generateKey('useFilters', 'space', id, groupIndex)}
          direction="vertical"
        >
          {isAnd != null && groupIndex !== 0 && (
            <>
              {handledFilterGroups.length > 1 && (
                <>
                  {isAnd ? (
                    <span>И</span>
                  ) : (
                    <Divider
                      orientation="left"
                      plain
                      orientationMargin={0}
                      style={{ margin: 0, padding: 0 }}
                    >
                      ИЛИ
                    </Divider>
                  )}
                </>
              )}
            </>
          )}

          <Space className="filters-and-buttons" size="small">
            <Space className="filter-selects-container">
              {innerWidth > 950 ? (
                <Dropdown
                  menu={{
                    items: filtersByVisits({
                      specificFilterId,
                      filterGroupIndex: groupIndex,
                    }),
                    triggerSubMenuAction: 'click',
                  }}
                  trigger={['click']}
                >
                  <Select
                    className="filter-by-visits"
                    style={memoizedFilterStyles}
                    placeholder={renderPlaceholder('По визитам', VISITS_FILTER)}
                  />
                </Dropdown>
              ) : (
                <span>
                  <AdaptedSelect
                    filterElements={filtersByVisits({
                      specificFilterId,
                      filterGroupIndex: groupIndex,
                    })}
                    placeholder={renderPlaceholder('По визитам', VISITS_FILTER)}
                    memoizedFilterStyles={memoizedFilterStyles}
                  />
                </span>
              )}

              {innerWidth > 950 ? (
                <Dropdown
                  menu={{
                    items: filtersByClients({
                      filterGroupsId: id,
                      filterGroupIndex: groupIndex,
                    }),
                    triggerSubMenuAction: 'click',
                  }}
                  trigger={['click']}
                >
                  <Select
                    className="filter-by-clients"
                    style={memoizedFilterStyles}
                    placeholder={renderPlaceholder(
                      'По клиентам',
                      CLIENTS_FILTER
                    )}
                  />
                </Dropdown>
              ) : (
                <span>
                  <AdaptedSelect
                    filterElements={filtersByClients({
                      filterGroupsId: id,
                      filterGroupIndex: groupIndex,
                    })}
                    placeholder={renderPlaceholder(
                      'По клиентам',
                      CLIENTS_FILTER
                    )}
                    memoizedFilterStyles={memoizedFilterStyles}
                  />
                </span>
              )}
            </Space>

            {innerWidth <= 950 ? (
              <Row gutter={8}>
                <Col span={12} style={{ width: `${contentWidth - 8}px` }}>
                  <Button
                    block
                    danger
                    type="default"
                    icon={<DeleteOutlined />}
                    onClick={() => deleteFilterGroups(id)}
                    disabled={handledFilterGroups.length <= 1}
                  >
                    {contentWidth <= 325 ? 'Удалить' : 'Удалить группу'}
                  </Button>
                </Col>
                <Col span={12} style={{ width: `${contentWidth - 8}px` }}>
                  {areLastFilterGroups && (
                    <Dropdown
                      menu={{ items: memoizedItems }}
                      trigger={['click']}
                    >
                      <Button block type="default" icon={<PlusOutlined />}>
                        {contentWidth <= 325 ? 'Добавить' : 'Добавить группу'}
                      </Button>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            ) : (
              <Space size="small">
                <Tooltip title="Удалить группу">
                  <Button
                    danger
                    type="default"
                    icon={<DeleteOutlined />}
                    onClick={() => deleteFilterGroups(id)}
                    disabled={handledFilterGroups.length <= 1}
                  />
                </Tooltip>
                {areLastFilterGroups && (
                  <Tooltip title="Добавить группу">
                    <Dropdown
                      menu={{ items: memoizedItems }}
                      trigger={['click']}
                    >
                      <Button type="default" icon={<PlusOutlined />} />
                    </Dropdown>
                  </Tooltip>
                )}
              </Space>
            )}
          </Space>
        </Space>
      );
    });
  }, [
    innerWidth,
    contentWidth,
    memoizedItems,
    memoizedFilterGroups,
    memoizedServerFilters,
    memoizedFilterStyles,
    deleteFilterGroups,
  ]);

  return {
    filterGroupsComponent,
    generateLeadsSegment,
    generateSegmentWithTwoGroups,
    generateSegmentNewData,
    generateSegmentRepeatedData,
    generateSegmentLostData,
  };
};
